import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import AuthShell from '@/components/feature/AuthShell';

export default function SignupPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { session, profile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (session && profile) {
      navigate(profile.status === 'approved' ? '/' : '/pending', { replace: true });
    }
  }, [session, profile, navigate]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('비밀번호는 6자 이상으로 입력해주세요.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('비밀번호가 서로 일치하지 않아요.');
      return;
    }

    setSubmitting(true);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: { data: { name: name.trim() } },
      });

      if (signUpError) {
        setError(
          signUpError.message.includes('already')
            ? '이미 가입된 이메일이에요. 로그인해주세요.'
            : '회원가입에 실패했어요. 잠시 후 다시 시도해주세요.',
        );
        return;
      }

      if (data.session) {
        navigate('/pending', { replace: true });
      } else {
        setDone(true);
      }
    } catch {
      setError('회원가입 중 문제가 발생했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthShell
        title="가입 신청이 완료됐어요"
        subtitle="확인 메일을 보내드렸어요."
        footer={
          <Link to="/login" className="text-sm font-semibold text-primary-600 hover:text-primary-700">
            로그인 화면으로 돌아가기
          </Link>
        }
      >
        <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-mail-check-line text-xl"></i>
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground-900">이메일 확인이 필요해요</p>
              <p className="text-xs text-foreground-600">{email}</p>
            </div>
          </div>
          <p className="text-sm leading-relaxed text-foreground-700">
            받은 메일함에서 인증 링크를 눌러 이메일 인증을 완료한 뒤 로그인해주세요. 인증 후에는
            운영자 승인을 기다리면 됩니다.
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="선생님 회원가입"
      subtitle="가입 후 운영자 승인이 완료되면 서비스를 이용할 수 있어요."
      footer={
        <p className="text-sm text-foreground-600">
          이미 계정이 있으신가요?{' '}
          <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700">
            로그인
          </Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
            이름
          </label>
          <input
            id="name"
            name="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="홍길동"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            이메일
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="example@email.com"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-foreground-800">
              비밀번호
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="6자 이상"
              className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <div>
            <label
              htmlFor="passwordConfirm"
              className="mb-1.5 block text-sm font-medium text-foreground-800"
            >
              비밀번호 확인
            </label>
            <input
              id="passwordConfirm"
              name="passwordConfirm"
              type="password"
              autoComplete="new-password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              required
              placeholder="다시 입력"
              className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-900">
            <i className="ri-error-warning-line mt-0.5"></i>
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="mt-1 flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
        >
          {submitting ? (
            <i className="ri-loader-4-line animate-spin"></i>
          ) : (
            <i className="ri-user-add-line"></i>
          )}
          가입 신청하기
        </button>
      </form>
    </AuthShell>
  );
}